import * as faceapi from '@vladmandic/face-api';
import { db } from './firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';

let modelsLoadedPromise = null;

export const faceVerificationService = {
  async loadModels() {
    if (modelsLoadedPromise) return modelsLoadedPromise;

    modelsLoadedPromise = (async () => {
      const localUrl = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/models` : '/models';

      try {
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(localUrl),
          faceapi.nets.ssdMobilenetv1.loadFromUri(localUrl),
          faceapi.nets.faceLandmark68Net.loadFromUri(localUrl),
          faceapi.nets.faceRecognitionNet.loadFromUri(localUrl)
        ]);
        console.log('✅ Modelos faciales cargados correctamente');
      } catch (err) {
        console.error('Error al cargar modelos faciales:', err);
        throw err;
      }
    })();

    return modelsLoadedPromise;
  },

  async detectFace(imageElement) {
    // 1. Intentar con TinyFaceDetector (alta velocidad y precisión en móviles)
    let detection = await faceapi
      .detectSingleFace(imageElement, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.25 }))
      .withFaceLandmarks()
      .withFaceDescriptor();

    // 2. Si no detecta, fallback a SSD MobileNet
    if (!detection) {
      detection = await faceapi
        .detectSingleFace(imageElement, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.25 }))
        .withFaceLandmarks()
        .withFaceDescriptor();
    }

    return detection;
  },

  async registerFace(userId, imageElement) {
    try {
      await this.loadModels();

      const detection = await this.detectFace(imageElement);

      if (!detection) {
        throw new Error('No se detectó ningún rostro. Asegúrate de encuadrar tu rostro de frente con buena iluminación.');
      }

      await updateDoc(doc(db, 'usuarios', userId), {
        faceDescriptor: Array.from(detection.descriptor),
        faceRegistered: true,
        faceRegisteredAt: new Date()
      });

      return { success: true, message: 'Rostro registrado exitosamente' };
    } catch (error) {
      console.error('Error al registrar rostro:', error);
      return { success: false, error: error.message };
    }
  },

  async verifyFace(userId, imageElement) {
    try {
      await this.loadModels();

      const userDoc = await getDoc(doc(db, 'usuarios', userId));
      if (!userDoc.exists()) {
        throw new Error('Usuario no encontrado en la base de datos');
      }

      const userData = userDoc.data();
      if (!userData.faceDescriptor) {
        throw new Error('Usuario no tiene rostro registrado. Primero registra tu rostro.');
      }

      const rawDescriptor = Array.isArray(userData.faceDescriptor)
        ? userData.faceDescriptor
        : Object.values(userData.faceDescriptor);

      if (!rawDescriptor || rawDescriptor.length === 0) {
        throw new Error('Descriptor facial no válido. Por favor registra tu rostro nuevamente.');
      }

      const detection = await this.detectFace(imageElement);

      if (!detection) {
        throw new Error('No se detectó ningún rostro en la cámara. Mira de frente con buena iluminación.');
      }

      const storedDescriptor = new Float32Array(rawDescriptor);
      const distance = faceapi.euclideanDistance(
        detection.descriptor,
        storedDescriptor
      );

      const threshold = 0.62;
      const verified = distance < threshold;
      const confidence = Math.max(0, Math.min(100, Math.round((1 - (distance / 0.7)) * 100)));

      if (!verified) {
        return {
          success: true,
          verified: false,
          confidence,
          distance: distance.toFixed(3),
          error: `Rostro no coincide (${confidence}%). Asegúrate de encuadrar bien tu rostro.`
        };
      }

      return { success: true, verified: true, confidence, distance };
    } catch (error) {
      console.error('Error al verificar rostro:', error);
      return { success: false, error: error.message };
    }
  },

  async hasFaceRegistered(userId) {
    try {
      const userDoc = await getDoc(doc(db, 'usuarios', userId));
      if (!userDoc.exists()) return false;
      const userData = userDoc.data();
      return Boolean(userData?.faceRegistered && userData?.faceDescriptor);
    } catch (error) {
      console.error('Error al comprobar registro facial:', error);
      return false;
    }
  }
};